import {
  AUTH_PATHS,
  AuthSession,
  CURRENT_PASSWORD_INCORRECT_MESSAGE,
  envelopeSchema,
  PasswordChangeStatus,
  type ErrorCode,
} from '@rp/contracts'
import { z } from 'zod'
import { defaultFetch } from '../api/client'
import { AuthClientError, type AuthClient, type AuthErrorKind, type AuthUser } from './client'
import { AUTH_COPY, AUTH_FAILURE_COPY } from './copy'

type State = { known: boolean; user: AuthUser | null; restoreFailure: AuthClientError | null }
type Listener = (user: AuthUser | null) => void
type FailureListener = (error: AuthClientError) => void
type Watcher = { onUser: Listener; onRestoreFailure: FailureListener | undefined }
type Failure = { status: number; code: ErrorCode | null; message: string | null }

const HTTP_BAD_REQUEST = 400
const HTTP_UNAUTHORIZED = 401
const HTTP_TOO_MANY = 429

export type SessionAuthOptions = { baseUrl: string; fetchImpl?: typeof fetch }

const Envelope = envelopeSchema(z.unknown())

const failureOf = async (response: Response): Promise<Failure> => {
  const parsed = Envelope.safeParse(await response.json().catch(() => null))
  const error = parsed.success ? parsed.data.error : null
  return { status: response.status, code: error?.code ?? null, message: error?.message ?? null }
}

const signInKind = (failure: Failure): AuthErrorKind => {
  if (failure.status === HTTP_UNAUTHORIZED) return 'invalid_credentials'
  if (failure.status === HTTP_TOO_MANY) return 'too_many_attempts'
  return failure.status === HTTP_BAD_REQUEST ? 'invalid_credentials' : 'unknown'
}

const passwordKind = (failure: Failure): AuthErrorKind => {
  if (failure.message === CURRENT_PASSWORD_INCORRECT_MESSAGE) return 'invalid_credentials'
  if (failure.status === HTTP_TOO_MANY) return 'too_many_attempts'
  return failure.status === HTTP_BAD_REQUEST ? 'weak_password' : 'unknown'
}

const errorOf = (kind: AuthErrorKind, message: string | null): AuthClientError =>
  new AuthClientError(kind, message ?? AUTH_COPY[kind])

const unknownError = (message: string | null, fallback: string): AuthClientError =>
  new AuthClientError('unknown', message ?? fallback)

const networkError = (cause: unknown): AuthClientError =>
  new AuthClientError('network', AUTH_COPY.network, { cause })

const asAuthError = (error: unknown, fallback: string): AuthClientError =>
  error instanceof AuthClientError ? error : unknownError(null, fallback)

const parsedData = async <T>(response: Response, schema: z.ZodType<T>): Promise<T> => {
  const parsed = envelopeSchema(schema).safeParse(await response.json().catch(() => null))
  if (!parsed.success || parsed.data.data === null) throw errorOf('unknown', null)
  return parsed.data.data as T
}

const restoredUserOf = async (response: Response): Promise<AuthUser | null> => {
  if (response.status === HTTP_UNAUTHORIZED) return null
  if (!response.ok) throw unknownError((await failureOf(response)).message, AUTH_FAILURE_COPY.restoreUnknown)
  const session = await parsedData(response, AuthSession).catch(() => {
    throw unknownError(null, AUTH_FAILURE_COPY.restoreUnknown)
  })
  return session.user
}

const confirmSignedOut = async (response: Response): Promise<void> => {
  if (response.ok || response.status === HTTP_UNAUTHORIZED) return
  throw unknownError((await failureOf(response)).message, AUTH_FAILURE_COPY.signOutUnknown)
}

export const createSessionAuthClient = (options: SessionAuthOptions): AuthClient => {
  const base = options.baseUrl.endsWith('/') ? options.baseUrl.slice(0, -1) : options.baseUrl
  const fetchImpl = options.fetchImpl ?? defaultFetch
  const slot: { state: State; watchers: readonly Watcher[]; restoring: Promise<void> | null } = {
    state: { known: false, user: null, restoreFailure: null },
    watchers: [],
    restoring: null,
  }

  const settle = (user: AuthUser | null): void => {
    slot.state = { known: true, user, restoreFailure: null }
    slot.watchers.forEach((watcher) => watcher.onUser(user))
  }

  const reportRestoreFailure = (error: AuthClientError): void => {
    slot.state = { ...slot.state, restoreFailure: error }
    slot.restoring = null
    slot.watchers.forEach((watcher) => watcher.onRestoreFailure?.(error))
  }

  const send = (path: string, method: 'GET' | 'POST', body?: unknown): Promise<Response> =>
    fetchImpl(`${base}${path}`, {
      method,
      credentials: 'same-origin',
      headers: { accept: 'application/json', ...(body === undefined ? {} : { 'content-type': 'application/json' }) },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    }).catch((cause: unknown) => {
      throw networkError(cause)
    })

  const restore = async (): Promise<void> => {
    const outcome = await send(AUTH_PATHS.session, 'GET')
      .then(restoredUserOf)
      .then(
        (user) => ({ user }),
        (error: unknown) => ({ error: asAuthError(error, AUTH_FAILURE_COPY.restoreUnknown) }),
      )
    if ('error' in outcome) reportRestoreFailure(outcome.error)
    else settle(outcome.user)
  }

  return {
    subscribe: (listener, onRestoreFailure) => {
      const watcher: Watcher = { onUser: listener, onRestoreFailure }
      slot.watchers = [...slot.watchers, watcher]
      if (slot.state.known) listener(slot.state.user)
      else if (slot.state.restoreFailure !== null) onRestoreFailure?.(slot.state.restoreFailure)
      else slot.restoring = slot.restoring ?? restore()
      return () => {
        slot.watchers = slot.watchers.filter((entry) => entry !== watcher)
      }
    },
    retryRestore: () => {
      if (slot.state.known || slot.restoring !== null) return
      slot.state = { ...slot.state, restoreFailure: null }
      slot.restoring = restore()
    },
    current: () => slot.state.user,
    signIn: async (email, password) => {
      const response = await send(AUTH_PATHS.signIn, 'POST', { email, password })
      if (!response.ok) {
        const failure = await failureOf(response)
        throw errorOf(signInKind(failure), failure.message)
      }
      const { user } = await parsedData(response, AuthSession)
      settle(user)
      return user
    },
    signOut: async () => {
      const response = await send(AUTH_PATHS.signOut, 'POST').catch((cause: unknown) => {
        throw new AuthClientError('network', AUTH_FAILURE_COPY.signOutNetwork, { cause })
      })
      await confirmSignedOut(response)
      settle(null)
    },
    forget: () => settle(null),
    changePassword: async (currentPassword, newPassword) => {
      const response = await send(AUTH_PATHS.password, 'POST', { currentPassword, newPassword })
      if (response.status === HTTP_UNAUTHORIZED) settle(null)
      if (!response.ok) {
        const failure = await failureOf(response)
        throw errorOf(passwordKind(failure), failure.message)
      }
      return parsedData(response, PasswordChangeStatus)
    },
  }
}
